import { Component, EventEmitter, Inject, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MweConstants } from 'src/app/constants/mwe-constants.service';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { MasterDataService } from 'src/app/services/master-data.service';
import Swal from 'sweetalert2';
import { SharedService } from 'src/app/services/shared-service.service';

@Component({
  selector: 'app-exhibitionfilter',
  templateUrl: './exhibitionfilter.component.html',
  styleUrls: ['./exhibitionfilter.component.scss']
})
export class ExhibitionfilterComponent {
  exhibitionFilterForm!: FormGroup;
  chips: any[] = [];
  existingChips: any;
  existingExhibitionFilterForm!: FormGroup;
  countryDropdown: any[] = [];
  stateDropdown: any[] = [];
  cityDropdown: any[] = [];
  statusDropdown: string[] = ['Archived', 'Upcoming'];
  companyNames: string[] = [];
  private originalCompanyNames: string[] = [];

  @Output() clearFilterData = new EventEmitter<any>();

  @ViewChild('autoCompleteCountryInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteCountryInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteStateInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteStateInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteCityInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteCityInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteStatusInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteStatusInput!: MatAutocompleteTrigger;
  @ViewChild('companyNamesAutoCompleteInput', { static: false, read: MatAutocompleteTrigger }) companyNamesAutoCompleteInput!: MatAutocompleteTrigger;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<any>,
    private masterDataService: MasterDataService,
    private fb: FormBuilder,
    private endCustomerService: EndCustomerService,
    private sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getCountries(null);
    this.getCompanyNames();
    
    if (this.data[0]) {
      this.exhibitionFilterForm.patchValue(this.data[0]);
      this.existingExhibitionFilterForm.patchValue(this.data[0]);
    } 
    if (this.data[1]) {
      this.chips = this.data[1];
      this.existingChips = this.data[1];
    }

    this.setupValueChangeHandlers();
  }

  initForm() {
    this.exhibitionFilterForm = this.fb.group({
      countries: [''],
      states: [''],
      cities: [''],
      date: [''],
      status: [''],
      companyName: [''],
    });
    this.existingExhibitionFilterForm = this.fb.group({
      countries: [''],
      states: [''],
      cities: [''],
      date: [''],
      status: [''],
      companyName: [''],
    });
  }

  setupValueChangeHandlers() {
    this.country.valueChanges.subscribe(value => {
      this.getCountries(value ? { startsWith: value } : null);
    });

    this.state.valueChanges.subscribe(value => {
      const country = this.country.value;
      const params = country ? { countryName: country, startsWith: value } : { startsWith: value };
      this.getStatesByCountryName(params);
    });

    this.city.valueChanges.subscribe(value => {
      const state = this.state.value;
      const params = state ? { stateName: state, startsWith: value } : { startsWith: value };
      this.getCitiesByStateName(params);
    });

    this.status.valueChanges.subscribe(value => {
      this.filterStatusOptions(value);
    });

    this.companyName.valueChanges.subscribe(value => {
      this.filterCompanyNameOptions(value);
    });
  }

  private filterStatusOptions(value: string | null): void {
    if (!value) {
      this.statusDropdown = ['Archived', 'Upcoming'];
      return;
    }
    const filterValue = value.toLowerCase();
    this.statusDropdown = ['Archived', 'Upcoming'].filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  private filterCompanyNameOptions(value: string | null): void {
    if (!value) {
      this.companyNames = [...this.originalCompanyNames];
      return;
    }
    const filterValue = value.toLowerCase();
    this.companyNames = this.originalCompanyNames.filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  ngAfterViewInit() {
    this.autoCompleteCountryInput?.panelClosingActions.subscribe(e => !e && this.country.setValue(null));
    this.autoCompleteStateInput?.panelClosingActions.subscribe(e => !e && this.state.setValue(null));
    this.autoCompleteCityInput?.panelClosingActions.subscribe(e => !e && this.city.setValue(null));
    this.autoCompleteStatusInput?.panelClosingActions.subscribe(e => !e && this.status.setValue(null));
    this.companyNamesAutoCompleteInput?.panelClosingActions.subscribe(e => !e && this.companyName.setValue(null));
  }

  getCountries(params: any) {
    this.fetchDropdownData(MweConstants.countryURL, params, 'countryDropdown');
  }

  getStatesByCountryName(params: any) {
    this.fetchDropdownData(MweConstants.stateUrl, params, 'stateDropdown');
  }

  getCitiesByStateName(params: any) {
    this.fetchDropdownData(MweConstants.cityUrl, params, 'cityDropdown');
  }

  getCompanyNames() {
    this.sharedService.getExhibitionCompanynames().subscribe(res => {
      this.companyNames = res?.data || [];
      this.originalCompanyNames = [...this.companyNames];
    });
  }

  dropdownMap = {
    countryDropdown: 'countryDropdown',
    stateDropdown: 'stateDropdown',
    cityDropdown: 'cityDropdown',
  } as const;

  fetchDropdownData(url: string, params: any, target: keyof typeof this.dropdownMap) {
    (this as any)[this.dropdownMap[target]] = [];
    this.masterDataService.executeGetRequestURL(url, params).subscribe(
      res => (this as any)[this.dropdownMap[target]] = res?.data || [],
      err => console.error(`Error fetching ${target}:`, err)
    );
  }

  get country() { return this.exhibitionFilterForm.get('countries') as FormControl; }
  get state() { return this.exhibitionFilterForm.get('states') as FormControl; }
  get city() { return this.exhibitionFilterForm.get('cities') as FormControl; }
  get date() { return this.exhibitionFilterForm.get('date') as FormControl; }
  get status() { return this.exhibitionFilterForm.get('status') as FormControl; }
  get companyName() { return this.exhibitionFilterForm.get('companyName') as FormControl; }

  onFilterSelected(value: any, type: string) {
    if (type === 'date') value = this.formatDate(value);
    if (!this.addChip(value, type)) return;

    if (type === 'countries') this.getStatesByCountryName({ countryName: value });
    if (type === 'states') this.getCitiesByStateName({ stateName: value });
  }

  addChip(name: string, type: string): boolean {
    this.chips = this.chips.filter(chip => chip.type !== type);
    this.chips.push({ name, type });
    return true;
  }

  remove(chip: any) {
    this.chips = this.chips.filter(c => c !== chip);
    this.exhibitionFilterForm.get(chip.type)?.setValue(null);
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.toLocaleString('default', { month: 'long' })} ${`0${date.getDate()}`.slice(-2)}, ${date.getFullYear()}`;
  }

  filterExhibitionCustomer() {
    const formData = this.exhibitionFilterForm.value;
    this.dialogRef.close([JSON.parse(JSON.stringify(formData)), this.chips]);
  }

  clearFilter() {
    this.exhibitionFilterForm.reset();
    this.chips = [];
    this.clearFilterData.emit(this.exhibitionFilterForm.value);
  }

  closeDialog() {
    this.clearFilter();
    this.filterExhibitionCustomer();
  }
}